import React, { useRef, useEffect, useState } from 'react';
import { GAME_CONSTANTS, COLORS } from '../constants';
import { useLives } from '../context/LivesContext';
import { useNavigate } from 'react-router-dom';

const {
  WIDTH: GAME_WIDTH,
  HEIGHT: GAME_HEIGHT,
  GRAVITY,
  LIFT,
  OBSTACLE_WIDTH,
  OBSTACLE_GAP,
  OBSTACLE_SPEED
} = GAME_CONSTANTS.HELICOPTER_GAME;

type GameState = 'idle' | 'playing' | 'gameOver';

const HelicopterGame: React.FC = () => {
  const { lives, addLives } = useLives();
  const canPlay = lives > 0;
  const [gameState, setGameState] = useState<GameState>('idle');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const scoreRef = useRef(0);
  const bestScoreRef = useRef(0);
  const helicopterRef = useRef({ x: 80, y: GAME_HEIGHT / 2, vy: 0 });
  const obstaclesRef = useRef<any[]>([]);
  const pressedRef = useRef(false);
  const [allowOneLastGame, setAllowOneLastGame] = useState(false);
  const navigate = useNavigate();

  // Reset game state
  const resetGame = () => {
    helicopterRef.current = { x: 80, y: GAME_HEIGHT / 2, vy: 0 };
    obstaclesRef.current = [
      { x: GAME_WIDTH, h: Math.random() * (GAME_HEIGHT - OBSTACLE_GAP - 100) + 50 },
      { x: GAME_WIDTH + 250, h: Math.random() * (GAME_HEIGHT - OBSTACLE_GAP - 100) + 50 },
    ];
    scoreRef.current = 0;
    pressedRef.current = false;
  };

  // Main game loop
  const draw = () => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    // Draw background
    ctx.fillStyle = '#222';
    ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    // Draw helicopter
    const heli = helicopterRef.current;
    ctx.save();
    ctx.translate(heli.x, heli.y);
    ctx.rotate(heli.vy * 0.05);
    ctx.fillStyle = COLORS.PRIMARY;
    ctx.fillRect(-20, -10, 40, 20); // body
    ctx.fillStyle = COLORS.WHITE;
    ctx.fillRect(10, -3, 15, 6); // tail
    ctx.restore();

    // Draw obstacles
    ctx.fillStyle = '#0f0';
    obstaclesRef.current.forEach((obs) => {
      ctx.fillRect(obs.x, 0, OBSTACLE_WIDTH, obs.h);
      ctx.fillRect(obs.x, obs.h + OBSTACLE_GAP, OBSTACLE_WIDTH, GAME_HEIGHT - obs.h - OBSTACLE_GAP);
    });

    // Draw score
    ctx.fillStyle = COLORS.WHITE;
    ctx.font = '24px monospace';
    ctx.fillText(`Score: ${scoreRef.current}`, 10, 30);
    ctx.fillText(`Best: ${bestScoreRef.current}`, 10, 60);

    // Draw game over screen
    if (gameState === 'gameOver') {
      ctx.fillStyle = COLORS.WHITE;
      ctx.font = '36px monospace';
      ctx.fillText('Game Over', GAME_WIDTH / 2 - 100, GAME_HEIGHT / 2);
      ctx.font = '20px monospace';
      ctx.fillText('Tap to Restart', GAME_WIDTH / 2 - 60, GAME_HEIGHT / 2 + 40);
    } else if (gameState === 'idle' && (canPlay || allowOneLastGame)) {
      // Tap to Play text
      ctx.font = '28px monospace';
      ctx.fillStyle = COLORS.WHITE;
      const tapText = '👆 Tap to Play!';
      const tapWidth = ctx.measureText(tapText).width;
      ctx.fillText(tapText, (GAME_WIDTH - tapWidth) / 2, GAME_HEIGHT / 2);
    }
  };

  // Game update logic
  const update = () => {
    if (gameState !== 'playing') return;
    
    const heli = helicopterRef.current;
    // Physics
    heli.vy += GRAVITY;
    if (pressedRef.current) heli.vy = LIFT;
    heli.y += heli.vy;

    // Move obstacles
    obstaclesRef.current.forEach((obs) => {
      obs.x -= OBSTACLE_SPEED;
    });
    // Add new obstacle
    if (obstaclesRef.current[0].x < -OBSTACLE_WIDTH) {
      obstaclesRef.current.shift();
      obstaclesRef.current.push({
        x: GAME_WIDTH,
        h: Math.random() * (GAME_HEIGHT - OBSTACLE_GAP - 100) + 50,
      });
      scoreRef.current++;
      if (scoreRef.current > bestScoreRef.current) bestScoreRef.current = scoreRef.current;
    }
    // Collision detection
    const heliRect = { x: heli.x - 20, y: heli.y - 10, w: 40, h: 20 };
    for (const obs of obstaclesRef.current) {
      // Top
      if (
        heliRect.x < obs.x + OBSTACLE_WIDTH &&
        heliRect.x + heliRect.w > obs.x &&
        heliRect.y < obs.h
      ) {
        console.log('Collision detected - top');
        setGameState('gameOver');
        return;
      }
      // Bottom
      if (
        heliRect.x < obs.x + OBSTACLE_WIDTH &&
        heliRect.x + heliRect.w > obs.x &&
        heliRect.y + heliRect.h > obs.h + OBSTACLE_GAP
      ) {
        console.log('Collision detected - bottom');
        setGameState('gameOver');
        return;
      }
    }
    // Out of bounds
    if (heli.y < 0 || heli.y > GAME_HEIGHT) {
      console.log('Out of bounds detected');
      setGameState('gameOver');
      return;
    }
  };

  // Start game
  const startGame = () => {
    console.log('=== START GAME CALLED ===');
    console.log('Starting game, current lives:', lives);
    console.log('Current gameState before start:', gameState);
    
    // Cancel any existing animation frame
    if (animationRef.current) {
      console.log('Cancelling existing animation frame');
      cancelAnimationFrame(animationRef.current);
      animationRef.current = undefined;
    }
    
    console.log('Resetting game...');
    resetGame();
    
    if (lives === 1) {
      setAllowOneLastGame(true);
    }
    
    console.log('Setting gameState to playing...');
    setGameState('playing');
    
    console.log('=== START GAME COMPLETED ===');
  };

  // Start game loop when gameState changes to 'playing'
  useEffect(() => {
    if (gameState === 'playing') {
      console.log('GameState changed to playing, starting loop...');
      loop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState]);

  // Main loop
  const loop = () => {
    console.log('Loop called, gameState:', gameState);
    update();
    draw();
    if (gameState === 'playing') {
      console.log('Requesting next animation frame');
      animationRef.current = requestAnimationFrame(loop);
    } else {
      console.log('Game not playing, stopping loop');
    }
  };

  // Input handlers
  useEffect(() => {
    console.log('Setting up event listeners, gameState:', gameState, 'canPlay:', canPlay);
    
    const handleDown = (e: KeyboardEvent | MouseEvent | TouchEvent) => {
      console.log('HandleDown called, canPlay:', canPlay, 'gameState:', gameState);
      
      pressedRef.current = true;
      
      // If game is not playing, start a new game and deduct a life
      if (gameState !== 'playing') {
        console.log('Deducting life, current lives before:', lives);
        addLives(-1); // Deduct 1 life when starting the game
        console.log('Life deducted, starting game');
        startGame();
      } else {
        console.log('Game already playing, just setting pressed');
      }
    };
    
    const handleUp = () => {
      console.log('HandleUp called');
      pressedRef.current = false;
    };
    
    const handleKeyDown = (e: KeyboardEvent) => {
      console.log('KeyDown:', e.code);
      if (e.code === 'Space') {
        e.preventDefault();
        handleDown(e);
      }
    };
    
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        handleUp();
      }
    };
    
    window.addEventListener('mousedown', handleDown);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchstart', handleDown);
    window.addEventListener('touchend', handleUp);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      console.log('Cleaning up event listeners');
      window.removeEventListener('mousedown', handleDown);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchstart', handleDown);
      window.removeEventListener('touchend', handleUp);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPlay, gameState, lives, addLives]);

  // Initial draw
  useEffect(() => {
    console.log('Initial draw effect, gameState:', gameState);
    draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState]);

  // When the game ends, if allowOneLastGame is true, set it to false
  useEffect(() => {
    if (gameState === 'gameOver' && allowOneLastGame) {
      setAllowOneLastGame(false);
    }
  }, [gameState, allowOneLastGame]);

  if (!canPlay && !allowOneLastGame) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#181818', borderRadius: 12, boxShadow: '0 2px 12px #0004', padding: 24, maxWidth: 440, margin: '0 auto' }}>
        <h2 style={{ color: COLORS.WHITE, marginBottom: 12 }}>🚁 Helicopter Game</h2>
        <div style={{ 
          background: '#333', 
          padding: '2rem', 
          borderRadius: 8, 
          textAlign: 'center',
          color: COLORS.WHITE,
          marginBottom: 16
        }}>
          <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>❌ No lives remaining!</p>
          <p style={{ fontSize: '0.9rem', color: '#aaa' }}>
            Complete quests to earn more lives and play again.
          </p>
        </div>
        <button
          onClick={() => navigate('/games')}
          style={{
            marginTop: 24,
            background: COLORS.PRIMARY,
            color: COLORS.WHITE,
            border: 'none',
            borderRadius: 8,
            padding: '0.8rem 2rem',
            fontSize: '1.1rem',
            fontWeight: 600,
            cursor: 'pointer',
            width: '100%',
            maxWidth: 300
          }}
        >
          Back to Games
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#181818', borderRadius: 12, boxShadow: '0 2px 12px #0004', padding: 24, maxWidth: 440, margin: '0 auto' }}>
      <h2 style={{ color: COLORS.WHITE, marginBottom: 12 }}>🚁 Helicopter Game</h2>
      <div style={{ 
        background: '#333', 
        padding: '0.5rem 1rem', 
        borderRadius: 8, 
        marginBottom: 16,
        color: COLORS.WHITE,
        fontSize: '0.9rem'
      }}>
        Cost: 1 ❤️ | Lives remaining: {lives}
      </div>
      <canvas
        ref={canvasRef}
        width={GAME_WIDTH}
        height={GAME_HEIGHT}
        style={{ border: `2px solid ${COLORS.PRIMARY}`, borderRadius: 8, background: '#222', marginBottom: 16 }}
      />
      {gameState === 'playing' && (
        <div style={{ color: '#aaa', fontSize: 16, marginBottom: 8 }}>Tap to Fly!</div>
      )}
      <button
        onClick={() => navigate('/games')}
        style={{
          marginTop: 24,
          background: COLORS.PRIMARY,
          color: COLORS.WHITE,
          border: 'none',
          borderRadius: 8,
          padding: '0.8rem 2rem',
          fontSize: '1.1rem',
          fontWeight: 600,
          cursor: 'pointer',
          width: '100%',
          maxWidth: 300
        }}
      >
        Back to Games
      </button>
    </div>
  );
};

export default HelicopterGame; 